import moment from "moment/moment";
import { useMessageSource } from "react-message-source";
import * as Yup from "yup";

export function useMandatoryTypeValidator(): Yup.MixedSchema {
  const { getMessage } = useMessageSource();
  return Yup.mixed().required(getMessage("Validation.message-required"));
}

export function useMandatoryStringTypeValidator(): Yup.StringSchema {
  const { getMessage } = useMessageSource();
  return Yup.string().required(getMessage("Validation.message-required"));
}

export function useAmountValidator(): Yup.NumberSchema {
  const { getMessage } = useMessageSource();

  const MAX_ALLOWED_AMOUNT = 999999;
  return Yup.number()
    .nullable()
    .transform((val) => (!val ? null : val))
    .required(getMessage("Validation.message-required"))
    .integer(getMessage("Validation.message-invalidAmount"))
    .positive(getMessage("Validation.message-invalidAmount"))
    .max(
      MAX_ALLOWED_AMOUNT,
      getMessage("Validation.message-maxAmount", MAX_ALLOWED_AMOUNT),
    );
}

export function useYesNoValidator(): Yup.StringSchema {
  const { getMessage } = useMessageSource();
  return Yup.string()
    .nullable()
    .oneOf(["yes", "no"], getMessage("Validation.message-required"))
    .required(getMessage("Validation.message-required"));
}

export function useDateValidator(): Yup.StringSchema {
  const { getMessage } = useMessageSource();

  return Yup.string()
    .required(getMessage("Validation.message-required"))
    .test(
      "isValidDate",
      getMessage("Validation.message-mustHaveSpecifiedFormat"),
      (date) => {
        // case falls outside of the scope of this test
        if (date === null || date === undefined) {
          return true;
        }

        // check for incomplete date, moment validator treats dates like '11.11.___ as valid
        if (!date || date.includes("_")) {
          return false;
        }

        const parsedDate = moment(date, "DD.MM.YYYY");
        return parsedDate.isValid();
      },
    )
    .test(
      "isDateInPast",
      getMessage("Validation.message-mustBePastDate"),
      (date) => {
        // case falls outside of the scope of this test
        if (date === null || date === undefined) {
          return true;
        }

        const now = moment();
        const parsedDate = moment(date, "DD.MM.YYYY");
        return parsedDate.isBefore(now);
      },
    )
    .nullable();
}

export const EMPTY_PARTIAL_DATE = "__.____";

export function usePartialDateValidator(): Yup.StringSchema {
  const { getMessage } = useMessageSource();

  return Yup.string()
    .test(
      "isValidPastDate",
      getMessage("Validation.message-mustHaveSpecifiedFormat"),
      (date) => {
        // case falls outside of the scope of this test
        if (
          date === null ||
          date === undefined ||
          date === EMPTY_PARTIAL_DATE
        ) {
          return true;
        }

        // check for incomplete date, moment validator treats dates like '11.11.___ as valid
        if (date.includes("_")) {
          return false;
        }

        const now = moment();
        const parsedDate = moment(`01.${date}`, "DD.MM.YYYY");
        return parsedDate.isValid() && parsedDate.isBefore(now);
      },
    )
    .nullable();
}
